/**
 * NdeAnalytics - Analytics dashboard for NDE tracking
 * 
 * Sections:
 * 1. Summary Cards (totals, rates, counts)
 * 2. Per-welder stats by NDE type (X-Ray, LPI, etc.) with checker & required % panels
 * 3. Failure Rate by Welder (ranked highest to lowest)
 * 4. Breakdown by Fluid Code
 * 5. Weekly Trend (inspections vs failures over time)
 * 6. Required NDE Percentages by Fluid Code
 */

import React, { useState, useEffect } from 'react';
import { useMsal } from '@azure/msal-react';
import { authenticatedApiCall } from '../../../utils/apiClient';
import { applyLetterhead } from '../../../utils/pdfLetterhead';
import { exportFilename } from '../../../utils/nomenclature';

let _jsPDF = null;
let _autoTable = null;
async function loadPdfLibs() {
  if (!_jsPDF) {
    const [jsPDFMod, autoTableMod] = await Promise.all([
      import('jspdf'),
      import('jspdf-autotable'),
    ]);
    _jsPDF = jsPDFMod.default;
    _autoTable = autoTableMod.default;
  }
  return { jsPDF: _jsPDF, autoTable: _autoTable };
}

let _ExcelJS = null;
async function loadExcelLib() {
  if (!_ExcelJS) {
    _ExcelJS = (await import('exceljs')).default;
  }
  return _ExcelJS;
}

const NdeAnalytics = ({ isOpen, onClose, projectId }) => {
  const { instance, accounts } = useMsal();
  const [loading, setLoading] = useState(true);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('report');
  const [data, setData] = useState({
    welderStats: [], requiredPercentages: [], overallStats: [],
    failureByWelder: [], fluidCodeStats: [], weeklyTrend: [], summary: {}
  });

  useEffect(() => {
    if (isOpen) fetchAnalytics();
  }, [isOpen]);

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      const response = await authenticatedApiCall(instance, accounts, 'GET', `/nde/analytics/${projectId}`);
      if (response.data.success) {
        setData(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching NDE analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleClientPdf = async () => {
    try {
      setPdfLoading(true);
      const response = await authenticatedApiCall(instance, accounts, 'GET', `/nde/project/${projectId}`);
      if (!response.data.success) throw new Error('Failed to fetch NDE data');

      const rows = response.data.data || [];

      const { jsPDF, autoTable } = await loadPdfLibs();
      const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'letter' });
      const pageW = doc.internal.pageSize.getWidth();
      const jobNumber = rows.length > 0 ? rows[0].job_number : '';
      const { y: letterheadY } = await applyLetterhead(doc, { instance, accounts, jobNumber, projectId, margin: 14 });

      doc.setFontSize(14);
      doc.setFont(undefined, 'bold');
      doc.setTextColor(30, 41, 59);
      doc.text('NDE Tracker Log', 14, letterheadY);

      const now = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
      doc.setFontSize(8);
      doc.setFont(undefined, 'normal');
      doc.setTextColor(100, 100, 100);
      doc.text(`Generated: ${now} | Records: ${rows.length}`, pageW - 14, letterheadY, { align: 'right' });

      const pdfColumns = [
        { header: '#', dataKey: 'idx' },
        { header: 'Date', dataKey: 'date' },
        { header: 'Welder #', dataKey: 'welder_number' },
        { header: 'NDE Type', dataKey: 'nde_type' },
        { header: 'ISO Sheet #', dataKey: 'iso_sheet_number' },
        { header: 'Line Number', dataKey: 'line_number' },
        { header: 'Fluid Code', dataKey: 'fluid_code' },
        { header: 'Qty Joint', dataKey: 'qty_joint' },
        { header: 'Qty NDE', dataKey: 'qty_nde' },
        { header: 'Qty Failed', dataKey: 'qty_failed' },
        { header: 'Comments', dataKey: 'comments' },
      ];

      const pdfRows = rows.map((r, i) => ({
        idx: i + 1,
        date: r.date ? new Date(r.date).toLocaleDateString() : '-',
        welder_number: r.welder_number || '-',
        nde_type: r.nde_type || '-',
        iso_sheet_number: r.iso_sheet_number || '-',
        line_number: r.line_number || '-',
        fluid_code: r.fluid_code || '-',
        qty_joint: r.qty_joint != null ? String(r.qty_joint) : '-',
        qty_nde: r.qty_nde != null ? String(r.qty_nde) : '-',
        qty_failed: r.qty_failed != null ? String(r.qty_failed) : '-',
        comments: r.comments || '',
      }));

      autoTable(doc, {
        columns: pdfColumns,
        body: pdfRows,
        startY: letterheadY + 6,
        styles: { fontSize: 7, cellPadding: 2 },
        headStyles: { fillColor: [30, 58, 95], textColor: 255, fontStyle: 'bold', fontSize: 7 },
        alternateRowStyles: { fillColor: [248, 250, 252] },
        columnStyles: {
          0: { cellWidth: 8, halign: 'center' },
          1: { cellWidth: 18 },
          7: { cellWidth: 14, halign: 'center' },
          8: { cellWidth: 14, halign: 'center' },
          9: { cellWidth: 14, halign: 'center' },
        },
        didParseCell: (cellData) => {
          if (cellData.section === 'body' && cellData.column.dataKey === 'qty_failed') {
            const val = parseInt(cellData.cell.raw);
            if (val > 0) {
              cellData.cell.styles.textColor = [220, 38, 38];
              cellData.cell.styles.fontStyle = 'bold';
            }
          }
        },
      });

      doc.save(exportFilename({ job: jobNumber, reportSlug: 'NDE_Log', ext: 'pdf' }));
    } catch (error) {
      console.error('Error generating client PDF:', error);
      alert('Failed to generate PDF. Please try again.');
    } finally {
      setPdfLoading(false);
    }
  };

  const [excelLoading, setExcelLoading] = useState(false);
  const [reportPdfLoading, setReportPdfLoading] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailAddresses, setEmailAddresses] = useState('');
  const [emailSending, setEmailSending] = useState(false);
  const [emailResult, setEmailResult] = useState(null);
  const [fluidTypeFilter, setFluidTypeFilter] = useState('X-Ray'); // Dylan: per-system % X-Rays complete

  const handleExcelExport = async () => {
    try {
      setExcelLoading(true);
      const ExcelJS = await loadExcelLib();
      const wb = new ExcelJS.Workbook();
      wb.creator = 'LGL Tools';
      wb.created = new Date();

      const num = (v) => { const n = parseFloat(v); return isNaN(n) ? 0 : n; };
      const HEADER_ARGB = 'FF1E3A5F';
      const dateStr = new Date().toISOString().slice(0, 10);

      const writeSheet = (name, columns, rows) => {
        const ws = wb.addWorksheet(name);
        const header = ws.getRow(1);
        columns.forEach((col, i) => {
          const cell = header.getCell(i + 1);
          cell.value = col.header;
          cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: HEADER_ARGB } };
          cell.font = { bold: true, color: { argb: 'FFFFFFFF' }, size: 10 };
          cell.alignment = { horizontal: i === 0 ? 'left' : 'center', vertical: 'middle', wrapText: true };
        });
        header.commit();
        rows.forEach((rowVals, ri) => {
          const row = ws.getRow(ri + 2);
          rowVals.forEach((v, i) => {
            const cell = row.getCell(i + 1);
            cell.value = v;
            cell.alignment = { horizontal: i === 0 ? 'left' : 'center' };
          });
          row.commit();
        });
        columns.forEach((col, i) => { ws.getColumn(i + 1).width = col.width || 16; });
        return ws;
      };

      const s = data.summary || {};
      writeSheet('Summary', [{ header: 'Metric', width: 24 }, { header: 'Value', width: 18 }], [
        ['Total ISOs', num(s.unique_isos)], ['Active Welders', num(s.unique_welders)], ['Fluid Codes', num(s.unique_fluid_codes)],
        ['Total Joints', num(s.total_joints)], ['Inspections Done', num(s.total_inspections)], ['Total Failures', num(s.total_failures)],
        ['Inspection Rate %', num(s.overall_inspection_rate)], ['Failure Rate %', num(s.overall_failure_rate)],
      ]);

      if ((data.welderStats || []).length > 0) {
        writeSheet('Welder Stats by Type',
          [{ header: 'NDE Type', width: 12 }, { header: 'Welder Number', width: 16 }, { header: 'Total Joints', width: 14 }, { header: 'NDE Taken', width: 14 }, { header: 'Failed', width: 12 }, { header: 'Percentage %', width: 14 }],
          data.welderStats.map(w => [w.nde_type || '', w.welder_number || '', num(w.total_joints), num(w.total_nde_taken), num(w.total_failed), Math.round(num(w.percentage) * 10) / 10]));
      }

      if ((data.failureByWelder || []).length > 0) {
        writeSheet('Failure by Welder',
          [{ header: 'Welder #', width: 16 }, { header: 'Total Inspections', width: 18 }, { header: 'Total Failures', width: 16 }, { header: 'Failure Rate %', width: 16 }],
          data.failureByWelder.map(w => [w.welder_number || '', num(w.total_inspections), num(w.total_failures), Math.round(num(w.failure_rate) * 10) / 10]));
      }

      if ((data.fluidCodeStats || []).length > 0) {
        writeSheet('By Fluid Code',
          [
            { header: 'Fluid Code', width: 16 }, { header: 'NDE Type', width: 12 }, { header: 'ISOs', width: 10 },
            { header: 'Total Joints', width: 14 }, { header: 'NDE Done', width: 12 },
            { header: '% Complete', width: 14 }, { header: 'Required %', width: 12 },
            { header: 'Target NDE', width: 12 }, { header: 'Remaining', width: 12 },
            { header: '% of Required', width: 14 }, { header: 'Failed', width: 10 },
            { header: 'Failure Rate %', width: 16 },
          ],
          data.fluidCodeStats.map(r => [
            r.fluid_code || '', r.nde_type || '', num(r.iso_count), num(r.total_joints), num(r.total_nde),
            Math.round(num(r.completion_rate) * 10) / 10, num(r.required_percentage),
            num(r.target_nde), num(r.remaining_nde),
            r.required_completion_rate != null ? Math.round(num(r.required_completion_rate) * 10) / 10 : '',
            num(r.total_failed), Math.round(num(r.failure_rate) * 10) / 10,
          ]));
      }

      if ((data.weeklyTrend || []).length > 0) {
        writeSheet('Weekly Trend',
          [{ header: 'Week Start', width: 16 }, { header: 'Joints', width: 12 }, { header: 'Inspections', width: 14 }, { header: 'Failures', width: 12 }],
          data.weeklyTrend.map(w => [w.week_start ? new Date(w.week_start).toLocaleDateString('en-CA') : '', num(w.joints), num(w.inspections), num(w.failures)]));
      }

      if ((data.requiredPercentages || []).length > 0) {
        writeSheet('Required %',
          [{ header: 'Fluid Code', width: 16 }, { header: 'NDE Type', width: 12 }, { header: 'Required %', width: 14 }],
          data.requiredPercentages.map(r => [r.fluid_code || '', r.nde_type || '', num(r.required_percentage)]));
      }

      const buf = await wb.xlsx.writeBuffer();
      const blob = new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = exportFilename({ job: projectId, reportSlug: 'NDE_Analytics', ext: 'xlsx' });
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error generating NDE Excel:', error);
      alert('Failed to generate Excel. Please try again.');
    } finally {
      setExcelLoading(false);
    }
  };

  const buildReportPdf = async () => {
    const { jsPDF, autoTable } = await loadPdfLibs();
    const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'letter' });
    const pageW = doc.internal.pageSize.getWidth();
    const pageH = doc.internal.pageSize.getHeight();
    const margin = 14;

    const { y: letterheadY, resolved } = await applyLetterhead(doc, { instance, accounts, projectId, margin });
    const jobNumber = resolved?.context?.job_number || '';

    const now = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
    doc.setFontSize(14);
    doc.setFont(undefined, 'bold');
    doc.setTextColor(30, 41, 59);
    doc.text('NDE Analytics Report', margin, letterheadY);
    doc.setFontSize(8);
    doc.setFont(undefined, 'normal');
    doc.setTextColor(100, 116, 139);
    doc.text(`Generated: ${now}`, pageW - margin, letterheadY, { align: 'right' });

    const s = data.summary || {};
    const welderStats = data.welderStats || [];
    const overallStats = data.overallStats || [];
    const requiredPercentages = data.requiredPercentages || [];
    const failureByWelder = data.failureByWelder || [];
    const fluidCodeStats = data.fluidCodeStats || [];
    const weeklyTrend = data.weeklyTrend || [];

    const byType = {};
    welderStats.forEach((row) => {
      const type = row.nde_type || 'Unknown';
      if (!byType[type]) byType[type] = [];
      byType[type].push(row);
    });
    const reqPctMap = {};
    requiredPercentages.forEach((r) => {
      const key = r.nde_type;
      if (!reqPctMap[key]) reqPctMap[key] = [];
      reqPctMap[key].push(r);
    });

    const typeRgb = {
      'X-Ray': [45, 106, 46],
      'LPI': [146, 64, 14],
      'MPI': [124, 58, 237],
      'UT': [30, 64, 175],
      'VT': [15, 118, 110],
      'PMI': [190, 24, 93],
    };

    const ensureSpace = (y, needed) => {
      if (y + needed > pageH - 12) {
        doc.addPage();
        return 16;
      }
      return y;
    };

    const drawCards = (startY, cards, cols) => {
      const gap = 4;
      const cardW = (pageW - margin * 2 - gap * (cols - 1)) / cols;
      const cardH = 16;
      cards.forEach((c, i) => {
        const col = i % cols;
        const row = Math.floor(i / cols);
        const x = margin + col * (cardW + gap);
        const y = startY + row * (cardH + gap);
        doc.setFillColor(248, 250, 252);
        doc.roundedRect(x, y, cardW, cardH, 2, 2, 'F');
        doc.setFillColor(...c.color);
        doc.rect(x, y, 1.5, cardH, 'F');
        doc.setFontSize(11);
        doc.setFont(undefined, 'bold');
        doc.setTextColor(...c.color);
        doc.text(String(c.value), x + 4, y + 7);
        doc.setFontSize(6.5);
        doc.setFont(undefined, 'normal');
        doc.setTextColor(100, 116, 139);
        doc.text(c.label, x + 4, y + 13);
      });
      return startY + Math.ceil(cards.length / cols) * (cardH + gap) + 2;
    };

    const drawBar = (y, title, rgb) => {
      y = ensureSpace(y, 12);
      doc.setFillColor(...rgb);
      doc.roundedRect(margin, y, pageW - margin * 2, 8, 1.5, 1.5, 'F');
      doc.setFontSize(9);
      doc.setFont(undefined, 'bold');
      doc.setTextColor(255, 255, 255);
      doc.text(String(title).toUpperCase(), pageW / 2, y + 5.5, { align: 'center' });
      return y + 8;
    };

    const tableOpts = {
      styles: { fontSize: 7.5, cellPadding: 1.6, halign: 'center' },
      alternateRowStyles: { fillColor: [248, 250, 252] },
      theme: 'grid',
    };

    let y = letterheadY + 6;
    y = drawCards(y, [
      { label: 'Total ISOs', value: s.unique_isos || 0, color: [59, 130, 246] },
      { label: 'Active Welders', value: s.unique_welders || 0, color: [139, 92, 246] },
      { label: 'Fluid Codes', value: s.unique_fluid_codes || 0, color: [6, 182, 212] },
      { label: 'Total Joints', value: s.total_joints || 0, color: [16, 185, 129] },
      { label: 'Inspections Done', value: s.total_inspections || 0, color: [245, 158, 11] },
      { label: 'Total Failures', value: s.total_failures || 0, color: [239, 68, 68] },
      { label: 'Inspection Rate', value: `${s.overall_inspection_rate || 0}%`, color: [99, 102, 241] },
      { label: 'Failure Rate', value: `${s.overall_failure_rate || 0}%`, color: [220, 38, 38] },
    ], 4);

    Object.entries(byType).forEach(([ndeType, welders]) => {
      const rgb = typeRgb[ndeType] || typeRgb['X-Ray'];
      const overall = overallStats.find((os) => os.nde_type === ndeType);
      const reqPcts = reqPctMap[ndeType] || [];
      const avgReqPct = reqPcts.length > 0
        ? reqPcts.reduce((sum, r) => sum + parseFloat(r.required_percentage), 0) / reqPcts.length
        : 5;

      y = drawBar(y + 2, `${ndeType} Total Number Joints Per Welder & Percentages`, rgb);
      autoTable(doc, {
        ...tableOpts,
        startY: y,
        margin: { left: margin, right: margin },
        head: [['#', 'Welder Number', 'Total Joints', `${ndeType}s Taken`, 'Failed', 'Percentage']],
        body: welders.map((w, idx) => [
          idx + 1,
          w.welder_number || '-',
          parseInt(w.total_joints) || 0,
          parseInt(w.total_nde_taken) || 0,
          parseInt(w.total_failed) || 0,
          `${parseFloat(w.percentage || 0).toFixed(1)}%`,
        ]),
        foot: overall ? [[
          { content: 'TOTALS / AVG %', colSpan: 2, styles: { halign: 'left', fontStyle: 'bold' } },
          parseInt(overall.total_joints) || 0,
          parseInt(overall.total_nde_taken) || 0,
          parseInt(overall.total_failed) || 0,
          `${parseFloat(overall.avg_percentage || 0).toFixed(1)}%`,
        ]] : undefined,
        headStyles: { fillColor: rgb, textColor: 255, fontStyle: 'bold', fontSize: 7.5 },
        footStyles: { fillColor: [240, 253, 244], textColor: rgb, fontStyle: 'bold' },
        columnStyles: { 1: { halign: 'left', fontStyle: 'bold' } },
        didParseCell: (cellData) => {
          if (cellData.section === 'body' && cellData.column.index === 4 && parseInt(cellData.cell.raw) > 0) {
            cellData.cell.styles.textColor = [220, 38, 38];
            cellData.cell.styles.fontStyle = 'bold';
          }
        },
      });
      y = doc.lastAutoTable.finalY + 4;

      y = ensureSpace(y, 40);
      const mid = pageW / 2;
      const checkerRows = welders.map((w) => {
        const pct = parseFloat(w.percentage || 0);
        return [w.welder_number || '-', pct < avgReqPct ? 'NEED MORE' : 'OK'];
      });
      const amountRows = welders.map((w) => {
        const totalJoints = parseInt(w.total_joints) || 0;
        const ndeTaken = parseInt(w.total_nde_taken) || 0;
        const needed = Math.ceil(totalJoints * (avgReqPct / 100));
        const remaining = Math.max(0, needed - ndeTaken);
        return [w.welder_number || '-', remaining > 0 ? `NEED ${remaining}` : 'COMPLETE'];
      });

      autoTable(doc, {
        ...tableOpts,
        startY: y,
        margin: { left: margin, right: mid + 2 },
        head: [[`${ndeType} Tracker - Checker`, '']],
        body: checkerRows,
        headStyles: { fillColor: rgb, textColor: 255, fontStyle: 'bold', fontSize: 7.5 },
        columnStyles: { 0: { halign: 'left', fontStyle: 'bold', cellWidth: 28 } },
        didParseCell: (cellData) => {
          if (cellData.section === 'body' && cellData.column.index === 1) {
            const ok = cellData.cell.raw === 'OK';
            cellData.cell.styles.textColor = ok ? [22, 163, 74] : [220, 38, 38];
            cellData.cell.styles.fontStyle = 'bold';
            cellData.cell.styles.fillColor = ok ? [240, 253, 244] : [254, 242, 242];
          }
        },
      });
      const checkerY = doc.lastAutoTable.finalY;

      autoTable(doc, {
        ...tableOpts,
        startY: y,
        margin: { left: mid + 2, right: margin },
        head: [[`Amount for ${avgReqPct}% - Round Up`, '']],
        body: amountRows,
        headStyles: { fillColor: [217, 119, 6], textColor: 255, fontStyle: 'bold', fontSize: 7.5 },
        columnStyles: { 0: { halign: 'left', fontStyle: 'bold', cellWidth: 28 } },
        didParseCell: (cellData) => {
          if (cellData.section === 'body' && cellData.column.index === 1) {
            const done = cellData.cell.raw === 'COMPLETE';
            cellData.cell.styles.textColor = done ? [22, 163, 74] : [220, 38, 38];
            cellData.cell.styles.fontStyle = 'bold';
          }
        },
      });
      y = Math.max(checkerY, doc.lastAutoTable.finalY) + 8;
    });

    if (failureByWelder.length > 0) {
      y = drawBar(y + 2, 'Failure Rate by Welder (All NDE Types)', [220, 38, 38]);
      autoTable(doc, {
        ...tableOpts,
        startY: y,
        margin: { left: margin, right: margin },
        head: [['Welder #', 'Total Inspections', 'Total Failures', 'Failure Rate']],
        body: failureByWelder.map((w) => [
          w.welder_number || '-',
          parseInt(w.total_inspections) || 0,
          parseInt(w.total_failures) || 0,
          `${(parseFloat(w.failure_rate) || 0).toFixed(1)}%`,
        ]),
        headStyles: { fillColor: [220, 38, 38], textColor: 255, fontStyle: 'bold', fontSize: 7.5 },
        columnStyles: { 0: { halign: 'left', fontStyle: 'bold' } },
        didParseCell: (cellData) => {
          if (cellData.section === 'body' && cellData.column.index === 2 && parseInt(cellData.cell.raw) > 0) {
            cellData.cell.styles.textColor = [220, 38, 38];
            cellData.cell.styles.fontStyle = 'bold';
          }
        },
      });
      y = doc.lastAutoTable.finalY + 8;
    }

    if (fluidCodeStats.length > 0) {
      y = drawBar(y + 2, 'NDE Breakdown by Fluid Code / System', [3, 105, 161]);
      autoTable(doc, {
        ...tableOpts,
        startY: y,
        margin: { left: margin, right: margin },
        head: [['Fluid Code', 'NDE Type', 'ISOs', 'Joints', 'NDE Done', '% Complete', 'Required %', 'Target', 'Remaining', '% of Required', 'Failed', 'Failure Rate']],
        body: fluidCodeStats.map((r) => {
          const reqPct = parseFloat(r.required_percentage) || 0;
          const remaining = parseInt(r.remaining_nde) || 0;
          const reqCompletion = r.required_completion_rate != null ? parseFloat(r.required_completion_rate) : null;
          return [
            r.fluid_code || '-',
            r.nde_type || '-',
            parseInt(r.iso_count) || 0,
            parseInt(r.total_joints) || 0,
            parseInt(r.total_nde) || 0,
            `${(parseFloat(r.completion_rate) || 0).toFixed(1)}%`,
            reqPct > 0 ? `${reqPct}%` : '—',
            reqPct > 0 ? (parseInt(r.target_nde) || 0) : '—',
            reqPct > 0 ? (remaining > 0 ? remaining : '✓') : '—',
            reqCompletion == null ? '—' : `${reqCompletion.toFixed(1)}%`,
            parseInt(r.total_failed) || 0,
            `${(parseFloat(r.failure_rate) || 0).toFixed(1)}%`,
          ];
        }),
        headStyles: { fillColor: [3, 105, 161], textColor: 255, fontStyle: 'bold', fontSize: 6.5 },
        styles: { fontSize: 6.5, cellPadding: 1.3, halign: 'center' },
        columnStyles: { 0: { halign: 'left', fontStyle: 'bold' } },
      });
      y = doc.lastAutoTable.finalY + 8;
    }

    if (weeklyTrend.length > 0) {
      y = drawBar(y + 2, 'Weekly NDE Trend', [79, 70, 229]);
      autoTable(doc, {
        ...tableOpts,
        startY: y,
        margin: { left: margin, right: margin },
        head: [['Week Start', 'Joints', 'Inspections', 'Failures']],
        body: weeklyTrend.map((w) => [
          w.week_start ? new Date(w.week_start).toLocaleDateString('en-CA', { year: 'numeric', month: 'short', day: 'numeric' }) : '-',
          parseInt(w.joints) || 0,
          parseInt(w.inspections) || 0,
          parseInt(w.failures) || 0,
        ]),
        headStyles: { fillColor: [79, 70, 229], textColor: 255, fontStyle: 'bold', fontSize: 7.5 },
        didParseCell: (cellData) => {
          if (cellData.section === 'body' && cellData.column.index === 3 && parseInt(cellData.cell.raw) > 0) {
            cellData.cell.styles.textColor = [220, 38, 38];
            cellData.cell.styles.fontStyle = 'bold';
          }
        },
      });
      y = doc.lastAutoTable.finalY + 8;
    }

    if (requiredPercentages.length > 0) {
      y = drawBar(y + 2, 'Required NDE Percentages by Fluid Code', [30, 58, 95]);
      autoTable(doc, {
        ...tableOpts,
        startY: y,
        margin: { left: margin, right: margin },
        head: [['Fluid Code', 'NDE Type', 'Required %']],
        body: requiredPercentages.map((r) => [r.fluid_code || '-', r.nde_type || '-', `${r.required_percentage}%`]),
        headStyles: { fillColor: [30, 58, 95], textColor: 255, fontStyle: 'bold', fontSize: 7.5 },
        columnStyles: { 0: { halign: 'left', fontStyle: 'bold' } },
      });
    }

    const filename = exportFilename({ job: jobNumber || projectId, reportSlug: 'NDE_Analytics', ext: 'pdf' });
    return { doc, filename, jobNumber };
  };

  const handleReportPdf = async () => {
    try {
      setReportPdfLoading(true);
      const { doc, filename } = await buildReportPdf();
      doc.save(filename);
    } catch (error) {
      console.error('Error generating NDE report PDF:', error);
      alert('Failed to generate PDF. Please try again.');
    } finally {
      setReportPdfLoading(false);
    }
  };

  const handleSendEmail = async () => {
    const recipients = emailAddresses.split(/[,;\s]+/).filter((e) => e.includes('@'));
    if (recipients.length === 0) {
      setEmailResult({ type: 'error', message: 'Enter at least one valid email address.' });
      return;
    }
    setEmailSending(true);
    setEmailResult(null);
    try {
      const { doc, filename } = await buildReportPdf();
      const pdfBase64 = doc.output('datauristring').split(',')[1];
      const response = await authenticatedApiCall(instance, accounts, 'POST', '/email/send', {
        to: recipients,
        subject: 'NDE Analytics Report',
        htmlBody: `<p>Please find attached the NDE analytics report (welder summary, inspection rates, and compliance).</p><p>Generated on ${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}.</p>`,
        attachments: [{ filename, fileblob: pdfBase64 }],
        feature: 'analytics_report',
      });
      if (response.data?.success) {
        setEmailResult({ type: 'success', message: `Sent to ${recipients.length} recipient(s).` });
        setTimeout(() => { setShowEmailModal(false); setEmailResult(null); setEmailAddresses(''); }, 2000);
      } else {
        setEmailResult({ type: 'error', message: response.data?.message || response.data?.error || 'Failed to send email.' });
      }
    } catch (error) {
      setEmailResult({ type: 'error', message: error?.response?.data?.message || error?.response?.data?.error || error?.message || 'Failed to send email.' });
    } finally {
      setEmailSending(false);
    }
  };

  if (!isOpen) return null;

  const { summary, welderStats, overallStats, requiredPercentages, failureByWelder, fluidCodeStats, weeklyTrend } = data;

  // Group welder stats by NDE type
  const byType = {};
  welderStats.forEach(row => {
    const type = row.nde_type || 'Unknown';
    if (!byType[type]) byType[type] = [];
    byType[type].push(row);
  });

  // Get required % per NDE type
  const reqPctMap = {};
  requiredPercentages.forEach(r => {
    const key = r.nde_type;
    if (!reqPctMap[key]) reqPctMap[key] = [];
    reqPctMap[key].push(r);
  });

  const typeColors = {
    'X-Ray': { header: '#2d6a2e', headerText: '#fff', bg: '#f0fdf4' },
    'LPI': { header: '#92400e', headerText: '#fff', bg: '#fffbeb' },
    'MPI': { header: '#7c3aed', headerText: '#fff', bg: '#faf5ff' },
    'UT': { header: '#1e40af', headerText: '#fff', bg: '#eff6ff' },
    'VT': { header: '#0f766e', headerText: '#fff', bg: '#f0fdfa' },
    'PMI': { header: '#be185d', headerText: '#fff', bg: '#fdf2f8' },
  };

  // ======== Summary Cards ========
  const renderSummaryCards = () => {
    const cards = [
      { label: 'Total ISOs', value: summary.unique_isos || 0, color: '#3b82f6' },
      { label: 'Active Welders', value: summary.unique_welders || 0, color: '#8b5cf6' },
      { label: 'Fluid Codes', value: summary.unique_fluid_codes || 0, color: '#06b6d4' },
      { label: 'Total Joints', value: summary.total_joints || 0, color: '#10b981' },
      { label: 'Inspections Done', value: summary.total_inspections || 0, color: '#f59e0b' },
      { label: 'Total Failures', value: summary.total_failures || 0, color: '#ef4444' },
      { label: 'Inspection Rate', value: `${summary.overall_inspection_rate || 0}%`, color: '#6366f1' },
      { label: 'Failure Rate', value: `${summary.overall_failure_rate || 0}%`, color: '#dc2626' },
    ];

    return (
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px', marginBottom: '24px' }}>
        {cards.map((card, i) => (
          <div key={i} style={{
            padding: '16px', borderRadius: '8px', border: '1px solid #e5e7eb',
            backgroundColor: '#fff', textAlign: 'center',
          }}>
            <div style={{ fontSize: '24px', fontWeight: '700', color: card.color }}>{card.value}</div>
            <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px', fontWeight: '500' }}>{card.label}</div>
          </div>
        ))}
      </div>
    );
  };

  // ======== Per-NDE-Type Table ========
  const renderTypeTable = (ndeType, welders) => {
    const colors = typeColors[ndeType] || typeColors['X-Ray'];
    const overall = overallStats.find(s => s.nde_type === ndeType);
    const reqPcts = reqPctMap[ndeType] || [];
    const avgReqPct = reqPcts.length > 0
      ? reqPcts.reduce((sum, r) => sum + parseFloat(r.required_percentage), 0) / reqPcts.length
      : 5;

    return (
      <div key={ndeType} style={{ marginBottom: '24px' }}>
        <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
          {/* Main Welder Table */}
          <div style={{ flex: '1 1 500px' }}>
            <div style={{
              backgroundColor: colors.header, color: colors.headerText,
              padding: '10px 16px', borderRadius: '8px 8px 0 0', textAlign: 'center',
              fontSize: '14px', fontWeight: '700', textTransform: 'uppercase',
            }}>
              {ndeType} Total Number Joints Per Welder & Percentages
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px', border: '1px solid #e5e7eb' }}>
              <thead>
                <tr style={{ backgroundColor: '#f8fafc' }}>
                  <th style={thStyle}>#</th>
                  <th style={thStyle}>Welder Number</th>
                  <th style={thStyle}>Total Joints</th>
                  <th style={thStyle}>{ndeType}s Taken</th>
                  <th style={thStyle}>Failed</th>
                  <th style={thStyle}>Percentage</th>
                </tr>
              </thead>
              <tbody>
                {welders.map((w, idx) => (
                  <tr key={idx} style={{ borderBottom: '1px solid #f3f4f6', backgroundColor: idx % 2 === 0 ? '#fff' : '#f9fafb' }}>
                    <td style={tdStyleCenter}>{idx + 1}</td>
                    <td style={{ ...tdStyle, fontWeight: '600' }}>{w.welder_number}</td>
                    <td style={tdStyleCenter}>{parseInt(w.total_joints) || 0}</td>
                    <td style={tdStyleCenter}>{parseInt(w.total_nde_taken) || 0}</td>
                    <td style={{ ...tdStyleCenter, color: parseInt(w.total_failed) > 0 ? '#dc2626' : '#6b7280' }}>
                      {parseInt(w.total_failed) || 0}
                    </td>
                    <td style={tdStyleCenter}>{parseFloat(w.percentage || 0).toFixed(1)}%</td>
                  </tr>
                ))}
                {welders.length === 0 && (
                  <tr><td colSpan={6} style={{ ...tdStyleCenter, padding: '20px', color: '#9ca3af' }}>No data yet</td></tr>
                )}
              </tbody>
              {overall && (
                <tfoot>
                  <tr style={{ backgroundColor: colors.bg, fontWeight: '700' }}>
                    <td colSpan={2} style={{ ...tdStyle, color: colors.header }}>TOTALS / AVG %</td>
                    <td style={tdStyleCenter}>{parseInt(overall.total_joints) || 0}</td>
                    <td style={tdStyleCenter}>{parseInt(overall.total_nde_taken) || 0}</td>
                    <td style={{ ...tdStyleCenter, color: '#dc2626' }}>{parseInt(overall.total_failed) || 0}</td>
                    <td style={tdStyleCenter}>{parseFloat(overall.avg_percentage || 0).toFixed(1)}%</td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>

          {/* Tracker & Amount Side Panel */}
          <div style={{ flex: '0 1 280px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {/* Tracker Checker */}
            <div>
              <div style={{
                backgroundColor: colors.header, color: colors.headerText,
                padding: '8px 12px', borderRadius: '8px 8px 0 0', textAlign: 'center',
                fontSize: '13px', fontWeight: '700',
              }}>
                {ndeType} Tracker - Checker
              </div>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px', border: '1px solid #e5e7eb' }}>
                <tbody>
                  {welders.map((w, idx) => {
                    const pct = parseFloat(w.percentage || 0);
                    const needsMore = pct < avgReqPct;
                    return (
                      <tr key={idx} style={{ borderBottom: '1px solid #f3f4f6' }}>
                        <td style={{ padding: '6px 10px', fontWeight: '500' }}>{w.welder_number}</td>
                        <td style={{
                          padding: '6px 10px', textAlign: 'center', fontWeight: '600',
                          backgroundColor: needsMore ? '#fef2f2' : '#f0fdf4',
                          color: needsMore ? '#dc2626' : '#16a34a',
                        }}>
                          {needsMore ? 'NEED MORE' : 'OK'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Amount for Required % */}
            <div>
              <div style={{
                backgroundColor: '#d97706', color: '#fff',
                padding: '8px 12px', borderRadius: '8px 8px 0 0', textAlign: 'center',
                fontSize: '13px', fontWeight: '700',
              }}>
                Amount for {avgReqPct}% - Round Up
              </div>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px', border: '1px solid #e5e7eb' }}>
                <tbody>
                  {welders.map((w, idx) => {
                    const totalJoints = parseInt(w.total_joints) || 0;
                    const ndeTaken = parseInt(w.total_nde_taken) || 0;
                    const needed = Math.ceil(totalJoints * (avgReqPct / 100));
                    const remaining = Math.max(0, needed - ndeTaken);
                    return (
                      <tr key={idx} style={{ borderBottom: '1px solid #f3f4f6' }}>
                        <td style={{ padding: '6px 10px', fontWeight: '500' }}>{w.welder_number}</td>
                        <td style={{
                          padding: '6px 10px', textAlign: 'center', fontWeight: '600',
                          color: remaining > 0 ? '#dc2626' : '#16a34a',
                        }}>
                          {remaining > 0 ? `NEED ${remaining}` : 'COMPLETE'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // ======== Failure Rate by Welder ========
  const renderFailureTable = () => {
    if (!failureByWelder || failureByWelder.length === 0) return null;

    const maxRate = Math.max(...failureByWelder.map(w => parseFloat(w.failure_rate) || 0), 1);

    return (
      <div style={{ marginBottom: '24px' }}>
        <div style={{
          backgroundColor: '#dc2626', color: '#fff',
          padding: '10px 16px', borderRadius: '8px 8px 0 0', textAlign: 'center',
          fontSize: '14px', fontWeight: '700', textTransform: 'uppercase',
        }}>
          Failure Rate by Welder (All NDE Types)
        </div>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px', border: '1px solid #e5e7eb' }}>
          <thead>
            <tr style={{ backgroundColor: '#f8fafc' }}>
              <th style={thStyle}>Welder #</th>
              <th style={thStyle}>Total Inspections</th>
              <th style={thStyle}>Total Failures</th>
              <th style={thStyle}>Failure Rate</th>
              <th style={{ ...thStyle, width: '200px' }}>Visual</th>
            </tr>
          </thead>
          <tbody>
            {failureByWelder.map((w, idx) => {
              const rate = parseFloat(w.failure_rate) || 0;
              const barWidth = maxRate > 0 ? (rate / maxRate) * 100 : 0;
              const barColor = rate >= 10 ? '#dc2626' : rate >= 5 ? '#f59e0b' : '#10b981';
              return (
                <tr key={idx} style={{ borderBottom: '1px solid #f3f4f6', backgroundColor: idx % 2 === 0 ? '#fff' : '#f9fafb' }}>
                  <td style={{ ...tdStyle, fontWeight: '600' }}>{w.welder_number}</td>
                  <td style={tdStyleCenter}>{parseInt(w.total_inspections) || 0}</td>
                  <td style={{ ...tdStyleCenter, color: parseInt(w.total_failures) > 0 ? '#dc2626' : '#6b7280' }}>
                    {parseInt(w.total_failures) || 0}
                  </td>
                  <td style={{ ...tdStyleCenter, fontWeight: '600', color: barColor }}>{rate.toFixed(1)}%</td>
                  <td style={{ padding: '8px 12px' }}>
                    <div style={{ width: '100%', backgroundColor: '#f3f4f6', borderRadius: '4px', height: '16px', overflow: 'hidden' }}>
                      <div style={{
                        width: `${barWidth}%`, height: '100%', backgroundColor: barColor,
                        borderRadius: '4px', transition: 'width 0.3s ease',
                      }} />
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  // ======== Fluid Code Breakdown ========
  const renderFluidCodeTable = () => {
    if (!fluidCodeStats || fluidCodeStats.length === 0) return null;

    const typeOptions = Array.from(new Set(fluidCodeStats.map((r) => r.nde_type).filter(Boolean)));
    if (!typeOptions.includes('All')) typeOptions.unshift('All');
    // Keep X-Ray first in the filter list when present
    const orderedTypes = ['All', 'X-Ray', ...typeOptions.filter((t) => t !== 'All' && t !== 'X-Ray')];

    const filtered = fluidTypeFilter === 'All'
      ? fluidCodeStats
      : fluidCodeStats.filter((r) => r.nde_type === fluidTypeFilter);

    const xraySummary = fluidCodeStats
      .filter((r) => r.nde_type === 'X-Ray')
      .reduce((acc, r) => {
        acc.joints += parseInt(r.total_joints) || 0;
        acc.nde += parseInt(r.total_nde) || 0;
        acc.systems += 1;
        acc.completeSystems += (parseFloat(r.required_completion_rate) >= 100 || (
          (parseFloat(r.required_percentage) || 0) === 0 && (parseInt(r.total_joints) || 0) > 0 && (parseInt(r.total_nde) || 0) >= (parseInt(r.total_joints) || 0)
        )) ? 1 : 0;
        return acc;
      }, { joints: 0, nde: 0, systems: 0, completeSystems: 0 });
    const xrayPct = xraySummary.joints > 0
      ? ((xraySummary.nde / xraySummary.joints) * 100).toFixed(1)
      : '0.0';

    return (
      <div style={{ marginBottom: '24px' }}>
        <div style={{
          backgroundColor: '#0369a1', color: '#fff',
          padding: '10px 16px', borderRadius: '8px 8px 0 0',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px',
          flexWrap: 'wrap',
        }}>
          <div style={{ fontSize: '14px', fontWeight: '700', textTransform: 'uppercase', textAlign: 'center', flex: 1 }}>
            NDE Breakdown by Fluid Code / System
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px' }}>
            <label htmlFor="fluid-type-filter" style={{ opacity: 0.9 }}>Show:</label>
            <select
              id="fluid-type-filter"
              value={fluidTypeFilter}
              onChange={(e) => setFluidTypeFilter(e.target.value)}
              style={{
                padding: '4px 8px', borderRadius: '6px', border: 'none',
                fontWeight: '600', fontSize: '12px', color: '#0f172a',
              }}
            >
              {orderedTypes.map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </div>
        </div>

        {fluidTypeFilter === 'X-Ray' && (
          <div style={{
            display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '8px',
            padding: '12px', background: 'linear-gradient(135deg, #f0fdf4 0%, #ecfdf5 100%)',
            borderLeft: '1px solid #e5e7eb', borderRight: '1px solid #e5e7eb',
          }}>
            {[
              { label: 'X-Ray Systems', value: xraySummary.systems },
              { label: 'Joints', value: xraySummary.joints },
              { label: 'X-Rays Done', value: xraySummary.nde },
              { label: 'Overall % Complete', value: `${xrayPct}%` },
            ].map((c) => (
              <div key={c.label} style={{
                background: '#fff', borderRadius: '8px', padding: '10px', textAlign: 'center',
                border: '1px solid #bbf7d0',
              }}>
                <div style={{ fontSize: '18px', fontWeight: '700', color: '#166534' }}>{c.value}</div>
                <div style={{ fontSize: '11px', color: '#4b5563', marginTop: '2px' }}>{c.label}</div>
              </div>
            ))}
          </div>
        )}

        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px', border: '1px solid #e5e7eb' }}>
          <thead>
            <tr style={{ backgroundColor: '#f8fafc' }}>
              <th style={thStyle}>Fluid Code</th>
              <th style={thStyle}>NDE Type</th>
              <th style={thStyle}>ISOs</th>
              <th style={thStyle}>Total Joints</th>
              <th style={thStyle}>NDE Done</th>
              <th style={thStyle}>% Complete</th>
              <th style={thStyle}>Required %</th>
              <th style={thStyle}>Target</th>
              <th style={thStyle}>Remaining</th>
              <th style={{ ...thStyle, minWidth: '120px' }}>% of Required</th>
              <th style={thStyle}>Failed</th>
              <th style={thStyle}>Failure Rate</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr>
                <td colSpan={12} style={{ ...tdStyleCenter, padding: '20px', color: '#6b7280' }}>
                  No rows for this NDE type
                </td>
              </tr>
            )}
            {filtered.map((row, idx) => {
              const completion = parseFloat(row.completion_rate) || 0;
              const reqPct = parseFloat(row.required_percentage) || 0;
              const target = parseInt(row.target_nde) || 0;
              const remaining = parseInt(row.remaining_nde) || 0;
              const reqCompletion = row.required_completion_rate != null
                ? parseFloat(row.required_completion_rate) || 0
                : null;
              const joints = parseInt(row.total_joints) || 0;
              const isDone = reqPct > 0 ? remaining === 0 && target > 0 : (joints > 0 && completion >= 100);
              const barColor = isDone ? '#16a34a' : completion >= 50 ? '#2563eb' : completion > 0 ? '#d97706' : '#94a3b8';
              const reqBarColor = reqCompletion == null ? '#cbd5e1'
                : reqCompletion >= 100 ? '#16a34a'
                : reqCompletion >= 50 ? '#2563eb'
                : '#d97706';

              return (
                <tr key={idx} style={{
                  borderBottom: '1px solid #f3f4f6',
                  backgroundColor: joints === 0 ? '#fafafa' : (idx % 2 === 0 ? '#fff' : '#f9fafb'),
                  opacity: joints === 0 ? 0.65 : 1,
                }}>
                  <td style={{ ...tdStyle, fontWeight: '600' }}>{row.fluid_code}</td>
                  <td style={tdStyleCenter}>
                    <span style={{
                      padding: '2px 8px', borderRadius: '12px', fontSize: '11px', fontWeight: '600',
                      backgroundColor: (typeColors[row.nde_type] || {}).bg || '#f3f4f6',
                      color: (typeColors[row.nde_type] || {}).header || '#374151',
                    }}>
                      {row.nde_type}
                    </span>
                  </td>
                  <td style={tdStyleCenter}>{parseInt(row.iso_count) || 0}</td>
                  <td style={tdStyleCenter}>{joints}</td>
                  <td style={tdStyleCenter}>{parseInt(row.total_nde) || 0}</td>
                  <td style={{ ...tdStyleCenter, fontWeight: '700', color: barColor }}>
                    {completion.toFixed(1)}%
                    <div style={{
                      marginTop: '4px', width: '100%', backgroundColor: '#e5e7eb',
                      borderRadius: '4px', height: '6px', overflow: 'hidden',
                    }}>
                      <div style={{
                        width: `${Math.min(100, completion)}%`, height: '100%',
                        backgroundColor: barColor, borderRadius: '4px',
                      }} />
                    </div>
                  </td>
                  <td style={tdStyleCenter}>{reqPct > 0 ? `${reqPct}%` : '—'}</td>
                  <td style={tdStyleCenter}>{reqPct > 0 ? target : '—'}</td>
                  <td style={{
                    ...tdStyleCenter, fontWeight: '600',
                    color: reqPct > 0 ? (remaining > 0 ? '#dc2626' : '#16a34a') : '#9ca3af',
                  }}>
                    {reqPct > 0 ? (remaining > 0 ? remaining : '✓') : '—'}
                  </td>
                  <td style={{ padding: '8px 12px' }}>
                    {reqCompletion == null ? (
                      <span style={{ color: '#9ca3af', fontSize: '12px' }}>—</span>
                    ) : (
                      <>
                        <div style={{ fontWeight: '700', color: reqBarColor, fontSize: '12px', marginBottom: '4px' }}>
                          {reqCompletion.toFixed(1)}%
                          {reqCompletion >= 100 && (
                            <span style={{
                              marginLeft: '6px', fontSize: '10px', padding: '1px 6px',
                              borderRadius: '999px', background: '#dcfce7', color: '#166534',
                            }}>COMPLETE</span>
                          )}
                        </div>
                        <div style={{
                          width: '100%', backgroundColor: '#e5e7eb',
                          borderRadius: '4px', height: '6px', overflow: 'hidden',
                        }}>
                          <div style={{
                            width: `${Math.min(100, reqCompletion)}%`, height: '100%',
                            backgroundColor: reqBarColor, borderRadius: '4px',
                          }} />
                        </div>
                      </>
                    )}
                  </td>
                  <td style={{ ...tdStyleCenter, color: parseInt(row.total_failed) > 0 ? '#dc2626' : '#6b7280' }}>
                    {parseInt(row.total_failed) || 0}
                  </td>
                  <td style={{ ...tdStyleCenter, fontWeight: '600', color: parseFloat(row.failure_rate) > 5 ? '#dc2626' : '#6b7280' }}>
                    {parseFloat(row.failure_rate || 0).toFixed(1)}%
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  // ======== Weekly Trend ========
  const renderWeeklyTrend = () => {
    if (!weeklyTrend || weeklyTrend.length === 0) return null;

    const maxInspections = Math.max(...weeklyTrend.map(w => parseInt(w.inspections) || 0), 1);

    return (
      <div style={{ marginBottom: '24px' }}>
        <div style={{
          backgroundColor: '#4f46e5', color: '#fff',
          padding: '10px 16px', borderRadius: '8px 8px 0 0', textAlign: 'center',
          fontSize: '14px', fontWeight: '700', textTransform: 'uppercase',
        }}>
          Weekly NDE Trend
        </div>
        <div style={{ border: '1px solid #e5e7eb', borderTop: 'none', padding: '16px', backgroundColor: '#fafafa' }}>
          {weeklyTrend.map((week, idx) => {
            const inspections = parseInt(week.inspections) || 0;
            const failures = parseInt(week.failures) || 0;
            const joints = parseInt(week.joints) || 0;
            const barWidth = (inspections / maxInspections) * 100;
            const failureWidth = maxInspections > 0 ? (failures / maxInspections) * 100 : 0;
            const weekLabel = new Date(week.week_start).toLocaleDateString('en-CA', { month: 'short', day: 'numeric' });

            return (
              <div key={idx} style={{ display: 'flex', alignItems: 'center', marginBottom: '8px', gap: '12px' }}>
                <div style={{ width: '80px', fontSize: '12px', fontWeight: '500', color: '#374151', textAlign: 'right' }}>
                  {weekLabel}
                </div>
                <div style={{ flex: 1, position: 'relative' }}>
                  <div style={{ width: '100%', backgroundColor: '#e5e7eb', borderRadius: '4px', height: '20px', overflow: 'hidden' }}>
                    <div style={{
                      width: `${barWidth}%`, height: '100%', backgroundColor: '#6366f1',
                      borderRadius: '4px', position: 'absolute', top: 0, left: 0,
                    }} />
                    {failures > 0 && (
                      <div style={{
                        width: `${failureWidth}%`, height: '100%', backgroundColor: '#ef4444',
                        borderRadius: '4px', position: 'absolute', top: 0, left: 0,
                      }} />
                    )}
                  </div>
                </div>
                <div style={{ width: '160px', fontSize: '11px', color: '#6b7280', display: 'flex', gap: '8px' }}>
                  <span>{joints}J</span>
                  <span style={{ color: '#6366f1' }}>{inspections} NDE</span>
                  {failures > 0 && <span style={{ color: '#ef4444' }}>{failures} fail</span>}
                </div>
              </div>
            );
          })}
          <div style={{ display: 'flex', gap: '16px', marginTop: '12px', justifyContent: 'center' }}>
            <span style={{ fontSize: '11px', display: 'flex', alignItems: 'center', gap: '4px' }}>
              <span style={{ width: '12px', height: '12px', backgroundColor: '#6366f1', borderRadius: '2px', display: 'inline-block' }} />
              Inspections
            </span>
            <span style={{ fontSize: '11px', display: 'flex', alignItems: 'center', gap: '4px' }}>
              <span style={{ width: '12px', height: '12px', backgroundColor: '#ef4444', borderRadius: '2px', display: 'inline-block' }} />
              Failures
            </span>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 9999,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <div style={{
        backgroundColor: '#fff', borderRadius: '12px', width: '1100px', maxHeight: '90vh',
        overflow: 'hidden', boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
        display: 'flex', flexDirection: 'column',
      }}>
        {/* Header */}
        <div style={{
          padding: '20px 24px', borderBottom: '1px solid #e5e7eb',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          background: 'linear-gradient(135deg, #1e3a5f 0%, #2d5a8e 100%)', color: 'white',
          flexShrink: 0,
        }}>
          <div>
            <h2 style={{ margin: 0, fontSize: '18px' }}>NDE Analytics Dashboard</h2>
            <p style={{ margin: '4px 0 0', fontSize: '13px', opacity: 0.9 }}>
              Inspection statistics, failure rates, and compliance tracking
            </p>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <button
              onClick={() => { setEmailResult(null); setShowEmailModal(true); }}
              disabled={loading || reportPdfLoading || emailSending}
              title="Email the Report page as a PDF"
              style={{
                background: 'rgba(255,255,255,0.2)', border: 'none', color: 'white',
                fontSize: '13px', fontWeight: '600', cursor: 'pointer',
                padding: '8px 14px', borderRadius: '6px', display: 'flex', alignItems: 'center', gap: '6px',
              }}
            >
              📧 Email
            </button>
            <button
              onClick={handleReportPdf}
              disabled={reportPdfLoading || loading}
              title="Download the Report page as a PDF (matches the on-screen layout)"
              style={{
                background: 'rgba(255,255,255,0.2)', border: 'none', color: 'white',
                fontSize: '13px', fontWeight: '600', cursor: reportPdfLoading ? 'wait' : 'pointer',
                padding: '8px 14px', borderRadius: '6px', display: 'flex', alignItems: 'center', gap: '6px',
              }}
            >
              {reportPdfLoading ? 'Generating...' : '📥 PDF'}
            </button>
            <button
              onClick={handleExcelExport}
              disabled={excelLoading || loading}
              title="Download analytics report as Excel"
              style={{
                background: 'rgba(255,255,255,0.2)', border: 'none', color: 'white',
                fontSize: '13px', fontWeight: '600', cursor: excelLoading ? 'wait' : 'pointer',
                padding: '8px 14px', borderRadius: '6px', display: 'flex', alignItems: 'center', gap: '6px',
              }}
            >
              {excelLoading ? 'Generating...' : '📊 Excel'}
            </button>
            <button onClick={onClose} style={{
              background: 'rgba(255,255,255,0.2)', border: 'none', color: 'white',
              fontSize: '20px', cursor: 'pointer', width: '32px', height: '32px',
              borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>&times;</button>
          </div>
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', borderBottom: '1px solid #e5e7eb', backgroundColor: '#f8fafc', flexShrink: 0 }}>
          {[
            { id: 'report', label: 'Report' },
            { id: 'client', label: 'Client' },
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => {
                setActiveTab(tab.id);
                if (tab.id === 'client') handleClientPdf();
              }}
              style={{
                padding: '12px 24px', border: 'none', cursor: 'pointer',
                fontSize: '13px', fontWeight: '600',
                backgroundColor: activeTab === tab.id ? '#fff' : 'transparent',
                color: activeTab === tab.id ? '#1e3a5f' : '#6b7280',
                borderBottom: activeTab === tab.id ? '2px solid #1e3a5f' : '2px solid transparent',
              }}
            >
              {tab.id === 'client' && pdfLoading ? 'Generating PDF...' : tab.label}
            </button>
          ))}
        </div>

        {/* Body */}
        <div style={{ padding: '24px', overflowY: 'auto', flex: 1 }}>
          {activeTab === 'client' ? (
            <div style={{ textAlign: 'center', padding: '60px', color: '#6b7280' }}>
              {pdfLoading ? (
                <>
                  <div style={{ fontSize: '16px', marginBottom: '8px' }}>Generating PDF...</div>
                  <div style={{ fontSize: '13px' }}>Preparing the NDE log for download</div>
                </>
              ) : (
                <>
                  <div style={{ fontSize: '16px', marginBottom: '12px' }}>Client PDF Export</div>
                  <div style={{ fontSize: '13px', marginBottom: '16px' }}>Click below to download the NDE log as a PDF document</div>
                  <button
                    onClick={handleClientPdf}
                    style={{
                      padding: '10px 24px', backgroundColor: '#1e3a5f', color: '#fff',
                      border: 'none', borderRadius: '6px', cursor: 'pointer',
                      fontSize: '14px', fontWeight: '600',
                    }}
                  >
                    Download PDF
                  </button>
                </>
              )}
            </div>
          ) : (
            <>
              {loading ? (
                <div style={{ textAlign: 'center', padding: '60px', color: '#6b7280' }}>
                  <div style={{ fontSize: '16px', marginBottom: '8px' }}>Loading analytics...</div>
                  <div style={{ fontSize: '13px' }}>Calculating statistics across all NDE records</div>
                </div>
              ) : Object.keys(byType).length === 0 && !summary.total_records ? (
                <div style={{ textAlign: 'center', padding: '60px', color: '#6b7280' }}>
                  <div style={{ fontSize: '16px', marginBottom: '8px' }}>No NDE data available yet</div>
                  <div style={{ fontSize: '13px' }}>Import ISOs and add welder/inspection data to see analytics</div>
                </div>
              ) : (
                <>
                  {renderSummaryCards()}
                  {Object.entries(byType).map(([type, welders]) => renderTypeTable(type, welders))}
                  {renderFailureTable()}
                  {renderFluidCodeTable()}
                  {renderWeeklyTrend()}
                  {requiredPercentages.length > 0 && (
                    <div style={{ padding: '16px', backgroundColor: '#f8fafc', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
                      <h3 style={{ margin: '0 0 12px', fontSize: '14px', fontWeight: '600' }}>Required NDE Percentages by Fluid Code</h3>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                        {requiredPercentages.map((r, i) => (
                          <span key={i} style={{
                            padding: '4px 12px', backgroundColor: '#e0e7ff', color: '#3730a3',
                            borderRadius: '16px', fontSize: '12px', fontWeight: '500',
                          }}>
                            {r.fluid_code}: {r.required_percentage}% ({r.nde_type})
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </div>
      </div>

      {showEmailModal && (
        <div
          style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 10001, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={(e) => { if (e.target === e.currentTarget && !emailSending) setShowEmailModal(false); }}
        >
          <div style={{ backgroundColor: 'white', borderRadius: '12px', padding: '1.5rem', width: '460px', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
            <h3 style={{ margin: '0 0 1rem', fontSize: '1.1rem', color: '#1e3a5f' }}>📧 Email NDE Report</h3>
            <p style={{ fontSize: '0.85rem', color: '#64748b', margin: '0 0 0.75rem' }}>
              Enter email addresses separated by commas. A PDF of the Report page (welder summary) will be attached.
            </p>
            <textarea
              value={emailAddresses}
              onChange={(e) => setEmailAddresses(e.target.value)}
              placeholder="email1@example.com, email2@example.com"
              style={{ width: '100%', minHeight: '80px', padding: '0.75rem', borderRadius: '8px', border: '1px solid #d1d5db', fontSize: '0.9rem', resize: 'vertical', boxSizing: 'border-box', fontFamily: 'inherit' }}
            />
            {emailResult && (
              <div style={{
                marginTop: '0.5rem', padding: '0.5rem 0.75rem', borderRadius: '6px', fontSize: '0.85rem',
                backgroundColor: emailResult.type === 'success' ? '#ecfdf5' : '#fef2f2',
                color: emailResult.type === 'success' ? '#059669' : '#dc2626',
              }}>
                {emailResult.message}
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '1rem' }}>
              <button
                onClick={() => { if (!emailSending) { setShowEmailModal(false); setEmailResult(null); } }}
                style={{ padding: '0.5rem 1rem', backgroundColor: '#f3f4f6', color: '#374151', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '0.85rem' }}
              >
                Cancel
              </button>
              <button
                onClick={handleSendEmail}
                disabled={emailSending || !emailAddresses.trim()}
                style={{ padding: '0.5rem 1rem', backgroundColor: emailSending ? '#9ca3af' : '#1e3a5f', color: 'white', border: 'none', borderRadius: '8px', cursor: emailSending ? 'wait' : 'pointer', fontSize: '0.85rem', fontWeight: '500' }}
              >
                {emailSending ? 'Sending...' : 'Send Email'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const thStyle = {
  padding: '8px 12px', textAlign: 'center', borderBottom: '2px solid #e5e7eb',
  fontSize: '12px', fontWeight: '700', color: '#374151', textTransform: 'uppercase',
};

const tdStyle = {
  padding: '8px 12px',
};

const tdStyleCenter = {
  padding: '8px 12px', textAlign: 'center',
};

export default NdeAnalytics;
